import React, { useEffect, useState } from "react";
import AxiosInstance from "../../../AxiosInstance.jsx";

import moment from "moment";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useUserType } from "../../../../contexts/UserTypeContext.jsx";

import {
  Grid,
  Typography,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Paper,
  Chip,
  Slide,
  Avatar,
} from "@mui/material";
import { Col } from "react-bootstrap";
import showToast from "../../../../components/Toast/Toster.jsx";
import BlueButton from "../../../../components/Button/BlueButton.jsx";
import WhiteButton from "../../../../components/Button/WhiteButton.jsx";
import {
  CheckCircle,
  FormatColorText,
  Schedule,
  DateRange,
  Today,
} from "@mui/icons-material";
import SettingsLayout from "../../../../components/Layouts/SettingsLayout.jsx";
import InputText from "../../../../components/InputFields/InputText.jsx";
import { getItem } from "../../../../utils/storage";
import { clearAuthCache } from "../../../../components/Login/Auth.jsx";

function DateFormat() {
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [customInputValue, setCustomInputValue] = useState("DD/MMM/YYYY");
  const [showPreview, setShowPreview] = useState(false);
  const [originalFormat, setOriginalFormat] = useState(null);
  const [originalCustomValue, setOriginalCustomValue] = useState(null);

  const handleFormatChange = (event) => {
    setSelectedFormat(event.target.value);
    if (event.target.value !== "custom") {
      setShowPreview(true);
      setTimeout(() => setShowPreview(false), 2000);
    }
  };

  const handleCustomDateChange = (event) => {
    const inputValue = event.target.value;
    const filteredValue = inputValue.replace(/[^a-zA-Z/-]/g, "");
    const upperCaseValue = filteredValue.toUpperCase();
    setCustomInputValue(upperCaseValue);

    if (upperCaseValue.length > 0) {
      setShowPreview(true);
      setTimeout(() => setShowPreview(false), 2000);
    }
  };

  const fetchDateFormat = async () => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.get(`/v1/theme/${CompanyId}`, {});
      if (response?.data?.statusCode === 200) {
        const format = response?.data?.data?.Format;

        if (!format || format === "" || format === null) {
          setSelectedFormat("MM/DD/YYYY");
          setOriginalFormat("MM/DD/YYYY");
          setOriginalCustomValue(null);
        } else {
          const predefinedFormats = ["MM/DD/YYYY", "YYYY-MM-DD", "YYYY-MMM-DD"];

          if (predefinedFormats.includes(format)) {
            setSelectedFormat(format);
            setOriginalFormat(format);
            setOriginalCustomValue(null);
          } else {
            setSelectedFormat("custom");
            setCustomInputValue(format);
            setOriginalFormat("custom");
            setOriginalCustomValue(format);
          }
        }
      } else {
        setSelectedFormat("MM/DD/YYYY");
        setOriginalFormat("MM/DD/YYYY");
        setOriginalCustomValue(null);
      }
    } catch (error) {
      console.error("Error fetching date format:", error.message);
      setSelectedFormat("MM/DD/YYYY");
      setOriginalFormat("MM/DD/YYYY");
      setOriginalCustomValue(null);
    }
  };

  const setDateFormat = async (Format, showToastMessage = true) => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.post(`/v1/theme/date-format`, {
        Format,
        CompanyId,
      });

      if (response?.data?.statusCode === 200) {
        // Update original format after successful save
        const predefinedFormats = ["MM/DD/YYYY", "YYYY-MM-DD", "YYYY-MMM-DD"];
        if (predefinedFormats.includes(Format)) {
          setOriginalFormat(Format);
          setOriginalCustomValue(null);
        } else {
          setOriginalFormat("custom");
          setOriginalCustomValue(Format);
        }
        clearAuthCache();
        if (showToastMessage) {
          showToast.success("Date format updated!", {
            position: "top-center",
            autoClose: 800,
          });
        }
      } else {
        if (showToastMessage) {
          showToast.error("Failed to update date format.", {
            position: "top-center",
            autoClose: 2000,
          });
        }
      }
    } catch (error) {
      console.error("Error updating date format:", error?.message);
      if (showToastMessage) {
        showToast.error("Error updating date format. Please try again.", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    }
  };

  useEffect(() => {
    fetchDateFormat();
  }, []);

  // Check if there are any changes from the original format
  const hasChanges = () => {
    // If original format hasn't been loaded yet, no changes detected
    if (originalFormat === null) {
      return false;
    }

    // If custom format is selected
    if (selectedFormat === "custom") {
      // Check if custom format was originally selected and if the value changed
      if (originalFormat === "custom") {
        return customInputValue.trim() !== (originalCustomValue || "").trim();
      }
      // Custom format is newly selected (different from original)
      return true;
    }

    // For predefined formats, check if it's different from original
    if (originalFormat === "custom") {
      // Original was custom, now it's a predefined format
      return true;
    }

    // Both are predefined formats, compare them
    return selectedFormat !== originalFormat;
  };

  const formatOptions = [
    {
      value: "MM/DD/YYYY",
      label: "MM/DD/YYYY",
      example: moment(new Date()).format("MM/DD/YYYY"),
      description: "US Standard",
      icon: <Today sx={{ fontSize: 18 }} />,
    },
    {
      value: "YYYY-MM-DD",
      label: "YYYY-MM-DD",
      example: moment(new Date()).format("YYYY-MM-DD"),
      description: "ISO Standard",
      icon: <DateRange sx={{ fontSize: 18 }} />,
    },
    {
      value: "YYYY-MMM-DD",
      label: "YYYY-MMM-DD",
      example: moment(new Date()).format("YYYY-MMM-DD"),
      description: "With Month Names",
      icon: <Schedule sx={{ fontSize: 18 }} />,
    },
    {
      value: "custom",
      label: "Custom Format",
      example: (() => {
        try {
          return moment(new Date()).format(customInputValue);
        } catch (error) {
          return "Invalid format";
        }
      })(),
      description: "Create Your Own",
      icon: <FormatColorText sx={{ fontSize: 18 }} />,
    },
  ];

  return (
    <>
      <Grid>
        <Grid className="">
            <SettingsLayout>
          <Col
            className="d-flex col-12 col-lg-12 col-md-12"
            style={{
              position: "relative",
              zIndex: "9",
              // borderLeft: "0.5px solid rgba(6, 49, 100, 30%)",
              // paddingLeft: "20px",
              // marginTop: "-30px",
            }}
            xl={12}
            lg={10}
            md={12}
          >
            <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%" }}>
              <Box sx={{ mb: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
              
                <Grid>
                  <Typography
                    className={`mt-3 mb-3 heading-three tableNameHead ${
                      isDonationMode ? "text-donor-color" : "text-blue-color"
                    }`}
                    style={{
                      fontWeight: 700,
                      textDecoration: "underline",
                      marginLeft: "5px",
                    }}
                  >
                    Date Format Settings
                  </Typography>
                </Grid>
              </Box>

              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  background: "#ffffff",
                  border: "1px solid #e0e0e0",
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: "100%",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      p: 2,
                      background: isDonationMode
                        ? "linear-gradient(135deg, #68b2cb 0%, #29B3E2 100%)"
                        : "linear-gradient(135deg, #0f2b46 0%, #0a4a8a 100%)",
                      color: "white",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Select Format
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Choose your preferred date format
                    </Typography>
                  </Box>

                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    {selectedFormat !== null && (
                    <RadioGroup
                      value={selectedFormat}
                      onChange={handleFormatChange}
                      sx={{ mb: 2 }}
                    >
                      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        {formatOptions.map((option) => (
                          <Grid
                            key={option.value}
                            size={{
                              xs: 12,
                              md: 6
                            }}>
                            <Paper
                              elevation={
                                selectedFormat === option.value ? 4 : 1
                              }
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                border:
                                  selectedFormat === option.value
                                    ? isDonationMode
                                      ? "2px solid #29B3E2"
                                      : "2px solid #0f2b46"
                                    : "1px solid #e0e0e0",
                                background:
                                  selectedFormat === option.value
                                    ? isDonationMode
                                      ? "linear-gradient(135deg, #E9F8FC 0%, #e6f3ff 100%)"
                                      : "linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)"
                                    : "#ffffff",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                                position: "relative",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    selectedFormat === option.value ? 6 : 3,
                                  borderColor: isDonationMode ? "#29B3E2" : "#0f2b46",
                                },
                              }}
                              onClick={() => setSelectedFormat(option.value)}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1.5,
                                    bgcolor:
                                      selectedFormat === option.value
                                        ? isDonationMode
                                          ? "#29B3E2"
                                          : "#0f2b46"
                                        : "#f5f5f5",
                                    color:
                                      selectedFormat === option.value
                                        ? "white"
                                        : isDonationMode
                                        ? "#29B3E2"
                                        : "#0f2b46",
                                  }}
                                >
                                  {option.icon}
                                </Box>

                                <Box sx={{ flexGrow: 1 }}>
                                  <FormControlLabel
                                    value={option.value}
                                    control={
                                      <Radio
                                        sx={{
                                          color: isDonationMode ? "#29B3E2" : "#0f2b46",
                                          "&.Mui-checked": { 
                                            color: isDonationMode ? "#29B3E2" : "#0f2b46" 
                                          },
                                          mt: -0.5,
                                          size: "small",
                                        }}
                                      />
                                    }
                                    label=""
                                  />

                                  <Typography
                                    variant="subtitle1"
                                    className={
                                      isDonationMode ? "text-donor-color" : "text-blue-color"
                                    }
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                  >
                                    {option.label}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#666",
                                      mb: 1,
                                      display: "block",
                                    }}
                                  >
                                    {option.description}
                                  </Typography>

                                  <Chip
                                    label={option.example}
                                    variant="filled"
                                    size="small"
                                    sx={{
                                      bgcolor:
                                        selectedFormat === option.value
                                          ? isDonationMode
                                            ? "#29B3E2"
                                            : "#0f2b46"
                                          : "#f0f0f0",
                                      color:
                                        selectedFormat === option.value
                                          ? "white"
                                          : isDonationMode
                                          ? "#29B3E2"
                                          : "#0f2b46",
                                      fontWeight: 500,
                                      fontSize: "0.75rem",
                                      height: 24,
                                    }}
                                  />
                                </Box>

                                {selectedFormat === option.value && (
                                  <CheckCircle
                                    sx={{
                                      color: "#66D59A",
                                      fontSize: 20,
                                      position: "absolute",
                                      top: 8,
                                      right: 8,
                                    }}
                                  />
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                    )}

                    {selectedFormat === "custom" && (
                      <Slide
                        direction="up"
                        in={selectedFormat === "custom"}
                        mountOnEnter
                        unmountOnExit
                      >
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            mb: 4,
                            background: isDonationMode
                              ? "linear-gradient(135deg, #E9F8FC 0%, #f0f9f4 100%)"
                              : "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)",
                            border: `1px solid ${isDonationMode ? "#29B3E2" : "var(--amber)"}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 2,
                            }}
                          >
                            <Avatar
                              sx={{ 
                                bgcolor: isDonationMode ? "#29B3E2" : "var(--amber)", 
                                width: 32, 
                                height: 32 
                              }}
                            >
                              <FormatColorText sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ 
                                  color: isDonationMode ? "#29B3E2" : "var(--amber)", 
                                  fontWeight: 600 
                                }}
                              >
                                Custom Format
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#666" }}
                              >
                                Define your own pattern
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <InputText
                              label="Enter format"
                              placeholder="e.g., DD/MMM/YYYY"
                              value={customInputValue}
                              onChange={handleCustomDateChange}
                              size="small"
                              fieldHeight="40px"
                              style={{ minWidth: 200 }}
                              className={
                                isDonationMode
                                  ? "date-format-custom-input--donor"
                                  : "date-format-custom-input--contractor"
                              }
                            />

                            {customInputValue?.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ 
                                    color: isDonationMode ? "#29B3E2" : "var(--amber)", 
                                    fontWeight: 600 
                                  }}
                                >
                                  Preview:
                                </Typography>
                                <Chip
                                  label={(() => {
                                    try {
                                      return moment(new Date()).format(
                                        customInputValue
                                      );
                                    } catch (error) {
                                      return "Invalid format";
                                    }
                                  })()}
                                  variant="filled"
                                  size="small"
                                  sx={{
                                    bgcolor: (() => {
                                      try {
                                        moment(new Date()).format(
                                          customInputValue
                                        );
                                        return isDonationMode ? "#29B3E2" : "var(--amber)";
                                      } catch (error) {
                                        return "var(--red)";
                                      }
                                    })(),
                                    color: "white",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                    height: 24,
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Slide>
                    )}

                    {/* Compact Preview Alert
                    <Fade in={showPreview}>
                      <Alert
                        severity="success"
                        size="small"
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                          "& .MuiAlert-icon": { color: "#66D59A" },
                        }}
                        icon={<CheckCircle />}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          Format updated!
                        </Typography>
                      </Alert>
                    </Fade> */}

<Box
                      sx={{
                        display: "flex",
                        gap: { xs: 1.5, sm: 2 },
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        alignItems: "center",
                        mt: { xs: 1, sm: 0 },
                      }}
                    >
                      <WhiteButton
                        onClick={async () => {
                          try {
                            // Reset to default format and save to backend
                            const defaultFormat = "MM/DD/YYYY";
                            
                            // Save to backend (this will update originalFormat and originalCustomValue)
                            await setDateFormat(defaultFormat, false);
                            
                            // Update local state after successful backend save
                            setSelectedFormat(defaultFormat);
                            setCustomInputValue("DD/MMM/YYYY");
                            
                            // Ensure original values are set correctly (setDateFormat should have done this, but ensure consistency)
                            setOriginalFormat(defaultFormat);
                            setOriginalCustomValue(null);

                            showToast.success("Date format reset to default successfully!", {
                              position: "top-center",
                              autoClose: 1500,
                            });
                          } catch (error) {
                            console.error("Error resetting format:", error);

                            showToast.error(
                              "Failed to reset date format. Please try again.",
                              {
                                position: "top-center",
                                autoClose: 2000,
                              }
                            );
                          }
                        }}
                        variant="outline"
                        type="button"
                        isDonationMode={isDonationMode}
                        label={
                          <>
                            <RestartAltIcon sx={{ mr: 1, fontSize: 20 }} />{" "}
                            Reset
                          </>
                        }
                      />
                      <BlueButton
                        id="payButton"
                        type="submit"
                        disabled={
                          !hasChanges() ||
                          (selectedFormat === "custom" &&
                            !customInputValue.trim())
                        }
                        Typography="Save Changes"
                        isDonationMode={isDonationMode}
                        onClick={async () => {
                          if (!selectedFormat) {
                            return;
                          }
                          if (
                            selectedFormat === "custom" &&
                            !customInputValue.trim()
                          ) {
                            showToast.error("Please enter a custom format", {
                              position: "top-center",
                              autoClose: 2000,
                            });
                            return;
                          }
                          if (!hasChanges()) {
                            return;
                          }
                          const formatToSave =
                            selectedFormat === "custom"
                              ? customInputValue
                              : selectedFormat;
                          await setDateFormat(formatToSave);
                        }}
                        label={"Save Changes"}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Col>
          </SettingsLayout>
        </Grid>
      </Grid>
    </>
  );
}

export default DateFormat;
